import React, { useEffect, lazy, Suspense } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { MessageCircle } from "lucide-react";
import ErrorBoundary from "@/components/ErrorBoundary";

// Lazy-loaded Admin Pages
const AdminLogin = lazy(() => import("@/pages/admin/Login"));
const AdminDashboard = lazy(() => import("@/pages/admin/Dashboard"));
const AdminProducts = lazy(() => import("@/pages/admin/Products"));
const AdminBlogs = lazy(() => import("@/pages/admin/Blog"));
const AdminCategories = lazy(() => import("@/pages/admin/Categories"));
const AdminContactSubmissions = lazy(
  () => import("@/pages/admin/ContactSubmissions"),
);
const AdminCalculatorSubmissions = lazy(
  () => import("@/pages/admin/CalculatorSubmissions"),
);
const AdminCalculator = lazy(() => import("@/pages/admin/Calculator"));
const AdminReports = lazy(() => import("@/pages/admin/Reports"));
const AdminWordPressImport = lazy(
  () => import("@/pages/admin/WordPressImport"),
);
const AdminWhyUs = lazy(() => import("@/pages/admin/WhyUs"));
const AdminSubCategories = lazy(() => import("@/pages/admin/SubCategories"));

// Scroll to top on route change
function ScrollToTop() {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
}

function App() {
  return (
    <BrowserRouter>
      <ErrorBoundary>
        <div className="App">
          <ScrollToTop />
          <Suspense
            fallback={
              <div className="flex items-center justify-center min-h-screen bg-gray-100">
                <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-yasu-dark border-t-transparent"></div>
              </div>
            }
          >
            <Routes>
              {/* Admin Routes */}
              <Route path="/" element={<AdminLogin />} />
              <Route path="/dashboard" element={<AdminDashboard />} />
              <Route path="/products" element={<AdminProducts />} />
              <Route path="/blogs" element={<AdminBlogs />} />
              <Route path="/categories" element={<AdminCategories />} />
              <Route path="/contacts" element={<AdminContactSubmissions />} />
              <Route
                path="/calculator-submissions"
                element={<AdminCalculatorSubmissions />}
              />
              <Route path="/calculator" element={<AdminCalculator />} />
              <Route path="/reports" element={<AdminReports />} />
              <Route
                path="/wordpress-import"
                element={<AdminWordPressImport />}
              />
              <Route path="/why-us" element={<AdminWhyUs />} />
              <Route path="/subcategories" element={<AdminSubCategories />} />
            </Routes>
          </Suspense>
          <Toaster position="top-right" richColors />

          {/* Global floating WhatsApp button */}
          <a
            href="https://wa.me/971564165775"
            target="_blank"
            rel="noopener noreferrer"
            className="fixed right-4 bottom-4 sm:right-6 sm:bottom-6 z-50 inline-flex items-center justify-center rounded-full bg-green-500 hover:bg-green-600 text-white shadow-lg hover:shadow-xl transition-all duration-300 w-12 h-12 sm:w-14 sm:h-14"
            aria-label="Chat on WhatsApp"
          >
            <MessageCircle size={24} className="sm:hidden" />
            <MessageCircle size={26} className="hidden sm:inline" />
          </a>
        </div>
      </ErrorBoundary>
    </BrowserRouter>
  );
}

export default App;
